import type { FC, HTMLAttributes } from 'react'
import styles from './circular-progress.module.css'

export interface CircularProgressProps extends HTMLAttributes<HTMLDivElement> {
  size?: number | string
  color?: 'primary' | 'secondary' | 'error' | 'inherit'
  variant?: 'indeterminate' | 'determinate'
  value?: number
}

const SIZE = 44
const CIRCUMFERENCE = 2 * Math.PI * ((SIZE - 4) / 2)

const colorMap: Record<string, string | undefined> = {
  secondary: styles.colorSecondary,
  error: styles.colorError,
  inherit: styles.colorInherit,
}

export const CircularProgress: FC<CircularProgressProps> = ({
  size = 40,
  color = 'primary',
  variant = 'indeterminate',
  value,
  className,
  style,
  ...rest
}) => {
  const classes = [styles.root, colorMap[color], variant === 'determinate' ? styles.determinate : undefined, className]
    .filter(Boolean)
    .join(' ')

  const circleStyle =
    variant === 'determinate'
      ? { strokeDasharray: `${CIRCUMFERENCE}px`, strokeDashoffset: `${CIRCUMFERENCE * (1 - (value ?? 0) / 100)}px` }
      : undefined

  return (
    <div
      role="progressbar"
      aria-valuenow={variant === 'determinate' ? (value ?? 0) : undefined}
      className={classes}
      data-motion-essential={variant === 'indeterminate' ? '' : undefined}
      style={{ width: size, height: size, ...style }}
      {...rest}
    >
      <svg width="100%" height="100%" viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}>
        <circle
          className={styles.circle}
          cx={SIZE}
          cy={SIZE}
          r={(SIZE - 4) / 2}
          fill="none"
          strokeWidth={3.6}
          style={circleStyle}
        />
      </svg>
    </div>
  )
}
